import React, { useEffect, useState } from 'react'
import { rbacService } from '../../services/rbac'

type RoleRecord = {
  id?: string
  protected?: boolean
  role: string
  label?: string
  description?: string
  features?: string[]
}

type FeatureRecord = {
  key: string
  label?: string
  group?: string
}

export const RBAC: React.FC = () => {
  const [roles, setRoles] = useState<RoleRecord[]>([])
  const [features, setFeatures] = useState<FeatureRecord[]>([])
  const [draft, setDraft] = useState<RoleRecord>({ role: '', label: '', description: '', features: [] })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')

  const loadRbac = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await rbacService.getRoles()
      setRoles((response?.data?.roles || []) as RoleRecord[])
      setFeatures((response?.data?.features || []) as FeatureRecord[])
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to load RBAC settings.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRbac()
  }, [])

  const toggleFeature = (key: string) => {
    setDraft((current) => ({
      ...current,
      features: current.features?.includes(key)
        ? current.features.filter((feature) => feature !== key)
        : [...(current.features || []), key],
    }))
  }

  const saveRole = async () => {
    if (!draft.role) {
      setError('Role key is required.')
      return
    }
    setLoading(true)
    setError('')
    try {
      await rbacService.saveRole(draft)
      setStatus('Role saved.')
      await loadRbac()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to save role.')
    } finally {
      setLoading(false)
    }
  }

  const deleteRole = async () => {
    if (!draft.role) return
    setLoading(true)
    setError('')
    try {
      await rbacService.deleteRole(draft.role)
      setDraft({ role: '', label: '', description: '', features: [] })
      setStatus('Role deleted.')
      await loadRbac()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to delete role.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <section className="panel workspace-panel stack" data-testid="workspace-panel">
      <div className="stack" style={{ gap: '.4rem' }}>
        <h2 className="heading-lg">Roles &amp; Access</h2>
        <p className="text-muted">Manage roles and canonical permissions in the selected tenant.</p>
      </div>

      <div className="inline-group">
        <button type="button" className="btn btn-secondary" onClick={loadRbac} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh roles'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => setDraft({ role: '', label: '', description: '', features: [] })} disabled={loading}>New role</button>
        <button type="button" className="btn btn-primary" onClick={saveRole} disabled={loading || draft.protected}>Save role</button>
        <button type="button" className="btn btn-secondary" onClick={deleteRole} disabled={!draft.role || loading || draft.protected}>Delete role</button>
      </div>

      {error ? <p className="error-text">{error}</p> : null}
      {status ? <p className="helper">{status}</p> : null}

      <div className="rbac-grid">
        <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)', overflowX: 'auto' }}>
          <h3 style={{ marginTop: 0 }}>Roles</h3>
          <table className="workspace-table">
            <thead><tr><th>Role</th><th>Description</th><th>Features</th></tr></thead>
            <tbody>
              {roles.length === 0 ? (
                <tr><td colSpan={3} className="helper">No roles found.</td></tr>
              ) : roles.map((role) => (
                <tr key={role.role} onClick={() => setDraft({ ...role, features: role.features || [] })}>
                  <td>{role.label || role.role}</td>
                  <td>{role.description || '-'}</td>
                  <td>{role.features?.length || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
          <h3 style={{ margin: 0 }}>Role editor</h3>
          {draft.protected ? <p className="helper">This built-in role cannot be edited here.</p> : null}
          <div className="form-row"><label htmlFor="roleKey">Role key</label><input id="roleKey" className="input" value={draft.role} disabled={Boolean(draft.id)} onChange={(event) => setDraft({ ...draft, role: event.target.value })} /></div>
          <div className="form-row"><label htmlFor="roleLabel">Label</label><input id="roleLabel" className="input" value={draft.label || ''} disabled={draft.protected} onChange={(event) => setDraft({ ...draft, label: event.target.value })} /></div>
          <div className="form-row"><label htmlFor="roleDescription">Description</label><textarea id="roleDescription" className="input" rows={3} value={draft.description || ''} disabled={draft.protected} onChange={(event) => setDraft({ ...draft, description: event.target.value })} /></div>
          <div className="stack" style={{ gap: '.4rem' }}>
            {features.map((feature) => (
              <label key={feature.key} className="inline-group">
                <input type="checkbox" checked={Boolean(draft.features?.includes(feature.key))} disabled={draft.protected} onChange={() => toggleFeature(feature.key)} />
                {feature.label || feature.key}
              </label>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
